import React from 'react';
import styled from '@emotion/styled';
import { Box, Container, Flex } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';

import getNights from '@shared/utils/getNights';

import Button from '@/components/Button';
import Heading from '@/components/Heading';
import PricePerTraveller from '@/components/PricePerTraveller';
import { FLIGHT_DIRECTION } from '@/constants';
import useBreakpoints, { isScreen } from '@/hooks/useBreakpoints';
import useCopyHeight from '@/hooks/useCopyHeight';
import useSearchQuery from '@/hooks/useSearchQuery';
import Charges from '@/propTypes/Charges';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { dataLayer } from '@/v2/utils/dataLayer';

const PriceBox = styled(Box)``;

const FlightSummary = React.memo(
  ({
    charges,
    onClicked,
    priceForAdult = false,
    hidePriceSubText = false,
    hidePerPersonPrice = false,
    direction,
  }) => {
    const shouldUseNewEvents = useGA4Events();
    const { searchQuery } = useSearchQuery();
    const { height, heightEffect } = useCopyHeight();
    const breakpoints = useBreakpoints();
    const isSmallScreen = isScreen(breakpoints)('xs', 'sm');

    const confirmButtonText = isSmallScreen
      ? 'Confirm'
      : `Confirm ${
          direction === FLIGHT_DIRECTION.Departing ? 'departing' : 'returning'
        } flight`;

    const onCtaClick = () => {
      if (shouldUseNewEvents) {
        dataLayer.ctaClickEvent({
          itemText: confirmButtonText,
        });
      }
      onClicked();
    };

    return (
      <Box height={height}>
        <Box
          ref={heightEffect}
          bg="white"
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          borderTop="1px solid"
          borderColor="border"
        >
          <Container px="3" mx={['0', null, 'auto']}>
            <Flex
              py={['4', null, '6']}
              alignItems="center"
              justifyContent={[null, null, 'flex-end']}
              bg="background.card"
              zIndex={10}
            >
              <PriceBox
                mr={['0', null, '10']}
                textAlign="right"
                width={['auto', null, 'auto']}
                flex={!isSmallScreen ? '' : '1 1 100%'}
              >
                <Heading.h5 color="primary" fontSize={['xs', null, 'sm']}>
                  {`Flights + ${getNights(searchQuery)} night stay`}
                </Heading.h5>
                <PricePerTraveller
                  // eslint-disable-next-line no-nested-ternary
                  perTraveller={
                    hidePriceSubText
                      ? undefined
                      : priceForAdult
                        ? charges.perAdult
                        : charges.perTraveller
                  }
                  total={charges.total}
                  travellers={searchQuery}
                  priceForAdult={priceForAdult}
                  hidePerPersonPrice={hidePerPersonPrice}
                  exactTotalPrice
                />
              </PriceBox>
              <Box
                flex={!isSmallScreen ? '0 1 auto' : ''}
                textAlign="right"
                width={[null, null, 'auto']}
                px={['3', null, '0']}
              >
                <Button
                  variant="primary"
                  onClick={onCtaClick}
                  data-testid="CONFIRM_FLIGHT_BUTTON"
                  large
                  width={['140px', null, '100%']}
                >
                  {confirmButtonText}
                </Button>
              </Box>
            </Flex>
          </Container>
        </Box>
      </Box>
    );
  },
);

FlightSummary.propTypes = {
  charges: PropTypes.shape(Charges),
  onClicked: PropTypes.func.isRequired,
  priceForAdult: PropTypes.bool,
  hidePerPersonPrice: PropTypes.bool,
  hidePriceSubText: PropTypes.bool,
};

export default FlightSummary;
